import {
  BaseDataGrid,
  ButtonRow,
  DATE_FORMAT_ISO_NO_TIME,
  Dayjs,
  getFilteredColDefs,
  SwitchField,
  useGridServerPagination,
  useSearchParamsBoolean,
  useSearchParamsDate,
  useSearchParamsForm,
} from "@synota-io/synota-shared-ui";
import { generatePath, Link, useLocation, useParams } from "react-router-dom";
import { IInvoice } from "../../../../api/hooks/useInvoices";
import {
  DataGridProps,
  GridColumnVisibilityModel,
  GridInputRowSelectionModel,
  GridPaginationModel,
} from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import { TableSummaryToolbar } from "../../../../shared/datagrid/TableSummaryToolbar";
import { TableDateFiltersToolbar } from "../../../../shared/datagrid/TableDateFiltersToolbar";
import { Button } from "@mui/material";
import { CONTRACT_INVOICES_PATH } from "../../../../paths";
import { useServiceChargePayments } from "../../../../api/hooks/useServiceChargePayments";
import { serviceChargePaymentFields } from "../fields/serviceChargePaymentFields";
import {
  DashboardCounterpartySearchField,
  DashboardCounterpartySearchOption,
} from "../../../../shared/form/DashboardCounterpartySearchField";
import { useSearchParamsDashboardCounterparty } from "../../users/hooks/useSearchParamsDashboardCounterparty";
import { useServiceChargePaymentsReport } from "../../../../api/hooks/useServiceChargePaymentsReport";

type Props = Pick<DataGridProps<IInvoice>, "slots" | "slotProps" | "onRowDoubleClick"> & {
  summary?: boolean;
};

export const ServiceChargePaymentsTable = ({ summary, ...props }: Props) => {
  const [rowSelectionModel, onRowSelectionModelChange] = useState<GridInputRowSelectionModel>([]);
  const [columnVisibility, setColumnVisibility] = useState<GridColumnVisibilityModel>({});

  const params = useParams<{
    id: string;
  }>();

  const { state } = useLocation();
  const { control, watch } = useSearchParamsForm<{
    start_date: Dayjs | null;
    end_date: Dayjs | null;
    counterparty: DashboardCounterpartySearchOption;
    failed_only: boolean | null;
  }>(
    {
      defaultValues: {
        counterparty: null,
        end_date: null,
        start_date: null,
        failed_only: false,
      },
    },
    {
      counterparty: useSearchParamsDashboardCounterparty({ uuid: params.id }),
      start_date: useSearchParamsDate(),
      end_date: useSearchParamsDate(),
      failed_only: useSearchParamsBoolean(),
    },
  );

  const values = watch();

  const [paginationModel, onPaginationModelChange] = useState<GridPaginationModel>(() =>
    summary
      ? { page: 0, pageSize: 5 }
      : {
          page: 0,
          pageSize: 30,
        },
  );

  const input = {
    user_uuid: params.id || "",
    counterparty_email: values.counterparty?.value,
    payment_date_start: values.start_date?.format(DATE_FORMAT_ISO_NO_TIME),
    payment_date_end: values.end_date?.format(DATE_FORMAT_ISO_NO_TIME),
    failed_only: values.failed_only,
  };

  const {
    payments,
    hasNextPage,
    isLoading: isLoadingInvoices,
  } = useServiceChargePayments({
    ...input,
    page: paginationModel.page,
    number_to_return: paginationModel.pageSize,
  });

  const { download, isPending: isPendingReport } = useServiceChargePaymentsReport(input);

  const paginationProps = useGridServerPagination({
    hasNextPage,
    currentPageRowCount: payments.length,
    paginationModel,
    onPaginationModelChange,
  });

  const columns = useMemo(
    () =>
      getFilteredColDefs(serviceChargePaymentFields, summary ? "summary" : "table", summary).map(
        (column) => ({
          ...column,
          flex: 1,
        }),
      ),
    [summary],
  );

  useEffect(() => {
    if (state && "selectedRow" in state && state.selectedRow) {
      onRowSelectionModelChange([state.selectedRow]);
    }
  }, [state]);

  const isLoading = isLoadingInvoices || isPendingReport;

  return (
    <BaseDataGrid
      {...props}
      slots={{
        footer: summary
          ? () => (
              <ButtonRow borderTop="solid" borderColor="background.default">
                <Button
                  component={Link}
                  to={generatePath(CONTRACT_INVOICES_PATH, { id: params.id! })}
                  size="small"
                  fullWidth
                >
                  See all
                </Button>
              </ButtonRow>
            )
          : undefined,
        toolbar: summary
          ? TableSummaryToolbar
          : (props) => (
              <TableDateFiltersToolbar
                onDownloadClick={download}
                control={control}
                isLoading={isLoading}
                {...props}
              >
                {params.id ? (
                  <DashboardCounterpartySearchField
                    sx={{ width: "24em" }}
                    disabled={isLoading}
                    size="small"
                    label="Payer"
                    variant="filled"
                    control={control}
                    name="counterparty"
                    uuid={params.id}
                  />
                ) : null}
                <SwitchField label="Failed Payments" name="failed_only" control={control} />
              </TableDateFiltersToolbar>
            ),
        ...props.slots,
      }}
      density={summary ? "compact" : "standard"}
      getRowId={(row) => row.shared_uuid}
      columns={columns}
      columnVisibilityModel={columnVisibility}
      onColumnVisibilityModelChange={setColumnVisibility}
      onRowSelectionModelChange={onRowSelectionModelChange}
      rowSelectionModel={rowSelectionModel}
      autosizeOnMount={!summary}
      {...paginationProps}
      loading={isLoading}
      rows={payments}
    />
  );
};
